'use client'

// React Imports
import type { ReactNode } from 'react'

// Third-party Imports
import { Refine } from '@refinedev/core'
import routerProvider from '@refinedev/nextjs-router'
import { MODULE_MANIFESTS } from '@coresoft/platform-contract'

// Util Imports
import { withSafeReturnTo } from '@/lib/safe-router'

// Provider Imports
import { accessControlProvider } from './access-control-provider'
import { authProvider } from './auth-provider'
import { dataProvider } from './data-provider'
import { i18nProvider } from './i18n-provider'
import { notificationProvider } from './notification-provider'

/*
 * Resources are COMPOSED from the module manifests, exactly like the sidebar.
 * A module's `resources[].name` is also its permission resource, which is what
 * lets accessControlProvider answer `can({ resource, action })` by string
 * concatenation instead of a lookup table.
 *
 * The list is not filtered by install state: Refine wants a stable resource map,
 * and a disabled module's routes are already closed by the API and hidden from
 * the sidebar. Registering one costs nothing and keeps route resolution honest.
 */
const resources = MODULE_MANIFESTS.flatMap(manifest =>
  manifest.resources.map(resource => ({
    name: resource.name,
    list: resource.list,
    show: resource.show,
    create: resource.create,
    edit: resource.edit,
    meta: {
      label: resource.label,
      canDelete: resource.canDelete ?? false,
      moduleKey: manifest.key
    }
  }))
)

/*
 * ! 🔴 `?to=` ЗӨВХӨН ЭНЭ САЙТЫН ЗАМ (FE-02). Refine-ийн `useLogin` нь
 * ! `parse().params.to`-г шалгалтгүй дагадаг; qs нь `to[]`, `to[0]`-г ч `to`
 * ! болгодог тул `SignInView`-ийн `get('to')` шалгалт тойрогддог байв.
 * ! Бүх hook уншдаг ганц цэг нь энэ — ороосон нь л бүгдийг хамарна.
 */
const safeRouterProvider = withSafeReturnTo(routerProvider)

const RefineProvider = ({ children }: { children: ReactNode }) => {
  return (
    <Refine
      routerProvider={safeRouterProvider}
      dataProvider={dataProvider}
      authProvider={authProvider}
      accessControlProvider={accessControlProvider}
      i18nProvider={i18nProvider}
      notificationProvider={notificationProvider}
      resources={resources}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
        disableTelemetry: true
      }}
    >
      {children}
    </Refine>
  )
}

export default RefineProvider
